import {
  ActionMenu,
  Button,
  CircularLoader,
  H4,
  Icon,
  Layout,
  Option,
  Title,
  Tooltip,
  useActions,
  useTheme,
  useToast,
  Stack,
  P4
} from "@introist/react-foundation/v2";
import { CollapseList, List, ListEmptyState, ListRow } from "components/organisms";
import { useNavigate } from "react-router-dom";
import { api, EmployeeDataSource } from "services/rpc/RpcProvider";
import { DataSourceIcon } from "../../components/DataSourceIcon";
import { RoundTag } from "../../../../components/RoundTag";
import _, { compact } from "lodash";
import styled from "styled-components";
import { DataSources, getDataSourceSpec } from "../../datasources";
import { useMemo, useState } from "react";
import { SyncPreviewModal } from "../../views/SyncPreviewModal";
import { formatDateTime } from "../../../../utils/DatesUtils";
import { DateFormats } from "../../../../utils/dates";
import { DataSourceRequestLogModal } from "../../containers/DataSourceRequestLogModal";
import { WebhookEditorModal } from "../../webhooks/WebhookEditor";
import { useDataSourceActions } from "../../hooks/useDataSources";

const StyledP4 = styled(P4)`
  font-style: italic;
  color: var(--palette-foreground-subdued);
`;

export const ConnectedDataSourcesList = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const toast = useToast();
  const { mutateAsync: deleteDataSource } = api.employees.dataSources.remove.useMutation({});
  const { mutateAsync: syncDataSourceConnection } = api.employees.dataSources.sync.useMutation({});
  const { mutateAsync: updateDataSource } = api.employees.dataSources.update.useMutation({});
  const { mutateAsync: reconnect } = api.employees.dataSources.reconnect.useMutation({});

  const { refetch } = api.employees.dataSources.list.useQuery({});
  const { onConfirmAction } = useActions();

  const { data: allSources, isLoading } = api.employees.dataSources.list.useQuery({});

  const { scanGroup } = useDataSourceActions();

  const [previewDs, setPreviewDs] = useState<EmployeeDataSource | undefined>();
  const [logDs, setLogDs] = useState<EmployeeDataSource | undefined>();

  const [webhookEditDs, setWebhookEditDs] = useState<string | null>(null);

  const dsGroups = useMemo(() => {
    return (allSources ?? []).filter(ds => ds.isGroup);
  }, [allSources]);

  const dataSources = useMemo(() => {
    return (allSources ?? []).filter(ds => !ds.isGroup && !ds.groupId);
  }, [allSources]);

  const isDatasetSyncOnly = (dataSource: EmployeeDataSource) =>
    !!DataSources.find(ds => ds.key === dataSource.sourceType)?.datasetSyncOnly;

  const openDataSource = (dataSource: EmployeeDataSource) =>
    isDatasetSyncOnly(dataSource) ? undefined : () => navigate(`/datasources/${dataSource.id}`);

  const getDeleteAction = (dataSource: EmployeeDataSource): Option => ({
    key: "delete-data-source",
    title: "Delete",
    startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
    titleStyles: {
      color: theme.palette.danger.default
    },
    onClick: onConfirmAction(
      async () => {
        await deleteDataSource({ dataSourceId: dataSource.id });
        await refetch();
      },
      {
        title: "Delete data source",
        description: "Are you sure you want to delete this data source?",
        confirmTitle: "Delete"
      }
    )
  });

  const getActions = (dataSource: EmployeeDataSource): Option[] => {
    // Dataset sync sources are synced from the dataset, so employee sync actions don't apply
    if (isDatasetSyncOnly(dataSource)) {
      return compact([
        {
          key: "edit-data-source-connection",
          title: "Edit connection",
          startAdornmentIcon: "pencil",
          onClick: () => navigate(`#edit=${dataSource.id}`, { relative: "path" })
        } as Option,
        getDataSourceSpec(dataSource.sourceType).updateCredentials
          ? ({
              key: "update-credentials",
              title: "Update credentials",
              startAdornmentIcon: "key",
              onClick: () => navigate(`#credentials=${dataSource.id}`, { relative: "path" })
            } as Option)
          : null,
        getDeleteAction(dataSource)
      ]);
    }

    const actions: Option[] = [];

    if (dataSource.active) {
      actions.push({
        key: "deactivate-data-source-connection",
        title: "Stop auto-sync",
        startAdornmentIcon: "pause",
        onClick: async () => {
          await updateDataSource({ dataSourceId: dataSource.id, updates: { active: false } });
          await refetch();
          toast.info("Data source inactivated");
        }
      });
    } else {
      actions.push({
        key: "activate-data-source-connection",
        title: "Activate auto-sync",
        startAdornmentIcon: "playCircle",
        onClick: async () => {
          await updateDataSource({ dataSourceId: dataSource.id, updates: { active: true } });
          await refetch();
          toast.success("Data source activated");
        }
      });
    }

    actions.push(
      ...compact([
        {
          key: "edit-data-source-connection",
          title: "Edit connection",
          startAdornmentIcon: "pencil",
          onClick: () => navigate(`#edit=${dataSource.id}`, { relative: "path" })
        } as Option,
        getDataSourceSpec(dataSource.sourceType).updateCredentials
          ? ({
              key: "update-credentials",
              title: "Update credentials",
              startAdornmentIcon: "key",
              onClick: () => navigate(`#credentials=${dataSource.id}`, { relative: "path" })
            } as Option)
          : null,
        DataSources.find(ds => ds.key === dataSource.sourceType)?.reconnect
          ? ({
              key: "reconnect",
              title: "Reconnect",
              startAdornmentIcon: "arrowsLeftRight",
              onClick: async () => {
                return reconnect({ id: dataSource.id }).then((res: any) => {
                  window.location = res.url;
                });
              }
            } as Option)
          : null,
        {
          key: "edit-data-source-filtering",
          title: "Edit filter",
          startAdornmentIcon: "filter",
          onClick: () => navigate(`#filter=${dataSource.id}`, { relative: "path" })
        } as Option,
        {
          key: "edit-data-source-create-condition",
          title: "Edit create condition",
          startAdornmentIcon: "filter",
          onClick: () => navigate(`#createcondition=${dataSource.id}`, { relative: "path" })
        } as Option,
        getDataSourceSpec(dataSource.sourceType).PreviewBuilder
          ? ({
              key: "ds-preview",
              title: "Preview sync",
              startAdornmentIcon: "preview",
              onClick: () => setPreviewDs(dataSource)
            } as Option)
          : null,
        {
          key: "sync-data-source-connection",
          title: "Sync now",
          startAdornmentIcon: "collapseDrawer",
          onClick: async () => {
            await syncDataSourceConnection(
              { dataSourceId: dataSource.id },
              {
                onSuccess: () => {
                  toast.success("Sync started, it might take a few minutes to complete");
                  refetch();
                },
                onError: () => toast.error("Starting sync failed")
              }
            );
          }
        } as Option,
        {
          key: "params",
          title: "Show params",
          startAdornmentIcon: "key",
          onClick: () => navigate(`#params=${dataSource.id}`, { relative: "path" })
        } as Option,
        {
          key: "request-log",
          title: "Request log",
          startAdornmentIcon: "list",
          onClick: () => setLogDs(dataSource)
        } as Option,
        {
          key: "webhook-config",
          title: "Webhook config",
          startAdornmentIcon: "targetAfter",
          onClick: () => setWebhookEditDs(dataSource.id)
        } as Option,
        getDeleteAction(dataSource)
      ])
    );

    return actions;
  };

  return (
    <Stack vertical>
      {dsGroups.map(dsGroup => (
        <CollapseList
          header={
            <Stack style={{ flex: 1 }}>
              <Stack>
                <DataSourceIcon dataSource={dsGroup.sourceType} />
                <H4>{dsGroup.name}</H4>
              </Stack>
              <ActionMenu
                options={[
                  {
                    key: "scan",
                    title: "Scan group",
                    startAdornmentIcon: "search",
                    onClick: () => scanGroup(dsGroup.id)
                  },
                  {
                    key: "delete",
                    title: "Delete",
                    startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
                    titleStyles: {
                      color: theme.palette.danger.default
                    },
                    onClick: onConfirmAction(
                      async () => {
                        await deleteDataSource({ dataSourceId: dsGroup.id });
                        await refetch();
                      },
                      {
                        title: "Are you sure to delete this group source?",
                        description: "All data sources in this group will be deleted.",
                        confirmTitle: "Delete"
                      }
                    )
                  }
                ]}
                attachToRef={false}
                style={{ marginLeft: "auto" }}
              />
            </Stack>
          }
          open={true}
        >
          {(allSources ?? [])
            .filter(ds => ds.groupId === dsGroup.id)
            .map(dataSource => (
              <ListRow
                key={dataSource.id}
                gridTemplateColumns="1fr auto"
                onClick={openDataSource(dataSource)}
              >
                <Stack gap="large">
                  <div style={{ width: "26px" }} />
                  <Stack gap="small">
                    <H4>{dataSource.name}</H4>
                    {!!dataSource.lastSync && (
                      <StyledP4>
                        {`Last sync ${formatDateTime(
                          dataSource.lastSync,
                          DateFormats.datetime.short
                        )}`}
                      </StyledP4>
                    )}
                  </Stack>
                </Stack>

                <Stack
                  justifyContent="flex-end"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {dataSource.sourceType === "csv-upload" && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPreviewDs(dataSource)}
                    >
                      Upload now
                    </Button>
                  )}
                  {dataSource.active && (
                    <Tooltip tooltip={`Auto-sync active`}>
                      <RoundTag
                        startAdornment={
                          <Icon name="recurring" color={theme.palette.success.default} />
                        }
                      ></RoundTag>
                    </Tooltip>
                  )}
                  {!_.isEqual(dataSource.filterCondition, {}) && (
                    <Tooltip tooltip={`Filter active`}>
                      <RoundTag
                        startAdornment={
                          <Icon name="filter" color={theme.palette.success.default} />
                        }
                        onClick={() => navigate(`#filter=${dataSource.id}`, { relative: "path" })}
                      ></RoundTag>
                    </Tooltip>
                  )}
                  {dataSource.mappedFieldCount ? (
                    <Tooltip tooltip={`${dataSource.mappedFieldCount} mapped fields`}>
                      <RoundTag
                        startAdornment={
                          <Icon name="arrowsLeftRight" color={theme.palette.success.default} />
                        }
                      >
                        <Title variant="bold">{dataSource.mappedFieldCount}</Title>
                      </RoundTag>
                    </Tooltip>
                  ) : null}

                  <ActionMenu options={getActions(dataSource)} maxHeight="500px" />
                </Stack>
              </ListRow>
            ))}
        </CollapseList>
      ))}
      {isLoading && <CircularLoader fillParent colorVariant="primary" style={{ flexGrow: 1 }} />}
      {!isLoading && dataSources && dataSources.length === 0 && (
        <ListEmptyState>
          <ListEmptyState.Title>No connected data sources</ListEmptyState.Title>
          <Title style={{ textAlign: "center" }}>
            Connect a data source to start synchronizing employee information
          </Title>
        </ListEmptyState>
      )}
      {!isLoading && dataSources && dataSources.length > 0 && (
        <List>
          {dataSources.map(dataSource => (
            <ListRow
              key={dataSource.id}
              gridTemplateColumns="1fr auto"
              onClick={openDataSource(dataSource)}
            >
              <Layout.Group gap="large">
                <DataSourceIcon dataSource={dataSource.sourceType} />
                <Stack gap="small">
                  <H4>{dataSource.name}</H4>
                  {!!dataSource.lastSync && (
                    <StyledP4>
                      {`Last sync ${formatDateTime(
                        dataSource.lastSync,
                        DateFormats.datetime.short
                      )}`}
                    </StyledP4>
                  )}
                </Stack>
              </Layout.Group>

              <Layout.Group
                justifyContent="flex-end"
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {dataSource.sourceType === "csv-upload" && (
                  <Button variant="outlined" size="small" onClick={() => setPreviewDs(dataSource)}>
                    Upload now
                  </Button>
                )}
                {dataSource.active && (
                  <Tooltip tooltip={`Auto-sync active`}>
                    <RoundTag
                      startAdornment={
                        <Icon name="recurring" color={theme.palette.success.default} />
                      }
                    ></RoundTag>
                  </Tooltip>
                )}
                {!_.isEqual(dataSource.filterCondition, {}) && (
                  <Tooltip tooltip={`Filter active`}>
                    <RoundTag
                      startAdornment={<Icon name="filter" color={theme.palette.success.default} />}
                      onClick={() => navigate(`#filter=${dataSource.id}`, { relative: "path" })}
                    ></RoundTag>
                  </Tooltip>
                )}
                {dataSource.mappedFieldCount ? (
                  <Tooltip tooltip={`${dataSource.mappedFieldCount} mapped fields`}>
                    <RoundTag
                      startAdornment={
                        <Icon name="arrowsLeftRight" color={theme.palette.success.default} />
                      }
                    >
                      <Title variant="bold">{dataSource.mappedFieldCount}</Title>
                    </RoundTag>
                  </Tooltip>
                ) : null}

                <ActionMenu options={getActions(dataSource)} maxHeight="500px" />
              </Layout.Group>
            </ListRow>
          ))}
        </List>
      )}
      <SyncPreviewModal
        open={!!previewDs}
        onClose={() => setPreviewDs(undefined)}
        dataSource={previewDs}
      />
      {webhookEditDs && (
        <WebhookEditorModal
          datasourceId={webhookEditDs}
          open={!!webhookEditDs}
          onClose={() => setWebhookEditDs(null)}
        />
      )}
      {logDs && (
        <DataSourceRequestLogModal
          open={!!logDs}
          onClose={() => setLogDs(undefined)}
          dsId={logDs!.id}
        />
      )}
    </Stack>
  );
};
